import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
} from 'react'
import { activitiesService } from '@/services/land/activitiesService'
import type { Activity, ActivityType } from '@popety-io/types'

interface ActivitiesContextState {
  // Data
  activities: Activity[]
  loading: boolean
  error: string | null
  total: number

  // Filters
  searchTerm: string
  setSearchTerm: (term: string) => void
  activeFilter: ActivityType | 'all'
  setActiveFilter: (filter: ActivityType | 'all') => void

  // Actions
  fetchActivities: (landId: string) => Promise<void>
  refreshActivities: () => Promise<void>

  // Current land ID
  currentLandId: string | null
}

const ActivitiesContext = createContext<ActivitiesContextState | undefined>(
  undefined
)

export const useActivities = () => {
  const context = useContext(ActivitiesContext)
  if (!context) {
    throw new Error('useActivities must be used within an ActivitiesProvider')
  }
  return context
}

interface ActivitiesProviderProps {
  children: React.ReactNode
}

export const ActivitiesProvider: React.FC<ActivitiesProviderProps> = ({
  children,
}) => {
  // Core state
  const [activities, setActivities] = useState<Activity[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [total, setTotal] = useState(0)
  const [currentLandId, setCurrentLandId] = useState<string | null>(null)

  // Filter state
  const [searchTerm, setSearchTerm] = useState('')
  const [activeFilter, setActiveFilter] = useState<ActivityType | 'all'>('all')

  // Fetch activities for a specific land
  const fetchActivities = useCallback(async (landId: string) => {
    try {
      setLoading(true)
      setError(null)
      setCurrentLandId(landId)

      const response = await activitiesService.getByLandId(landId)
      setActivities(response.data || [])
      setTotal(response.data?.length || 0)
    } catch (err) {
      console.error('Failed to fetch activities:', err)
      setError('Failed to load activities')
      setActivities([])
      setTotal(0)
    } finally {
      setLoading(false)
    }
  }, [])

  // Refresh activities for current land
  const refreshActivities = useCallback(async () => {
    if (currentLandId) {
      await fetchActivities(currentLandId)
    }
  }, [currentLandId, fetchActivities])

  // Reset filters when land changes
  useEffect(() => {
    setSearchTerm('')
    setActiveFilter('all')
  }, [currentLandId])

  const contextValue: ActivitiesContextState = {
    // Data
    activities,
    loading,
    error,
    total,

    // Filters
    searchTerm,
    setSearchTerm,
    activeFilter,
    setActiveFilter,

    // Actions
    fetchActivities,
    refreshActivities,

    // Current land ID
    currentLandId,
  }

  return (
    <ActivitiesContext.Provider value={contextValue}>
      {children}
    </ActivitiesContext.Provider>
  )
}
